import React from "react";
import {
  DEAL_TYPES,
  DealTypeId,
  FilterSelection,
  dealLabelFor,
} from "../../utils/offerFilters";

interface FiltersDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  /** Property types present in the ads, in display order. */
  propertyTypes: string[];
  selection: FilterSelection;
  /** Check or uncheck a property type row. */
  onToggleType: (propertyType: string) => void;
  /** Pick the deal type for a row; checks the row if it was unchecked. */
  onPickDeal: (propertyType: string, deal: DealTypeId) => void;
  isLoading: boolean;
  showRequests: boolean;
  onToggleRequests: () => void;
  showWallets: boolean;
  onToggleWallets: () => void;
}

const CheckBox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={`flex items-center justify-center w-6 h-6 rounded border-2 border-solid flex-shrink-0 text-sm ${
      checked
        ? "border-primaryColor100 bg-primaryColor100 text-white"
        : "border-gray-400 text-transparent"
    }`}
  >
    ✓
  </span>
);

const Radio: React.FC<{ checked: boolean; label: string; onClick: () => void }> = ({
  checked,
  label,
  onClick,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onClick}
    className="flex flex-row items-center gap-1 bg-transparent p-0"
  >
    <span
      className={`flex items-center justify-center w-4 h-4 rounded-full border-2 border-solid flex-shrink-0 ${
        checked ? "border-primaryColor100" : "border-gray-400"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full ${checked ? "bg-primaryColor100" : "bg-transparent"}`}
      />
    </span>
    <span
      className={`text-sm whitespace-nowrap ${
        checked ? "text-primaryColor100" : "text-gray-500"
      }`}
    >
      {label}
    </span>
  </button>
);

const FiltersDropdown: React.FC<FiltersDropdownProps> = ({
  isOpen,
  onClose,
  propertyTypes,
  selection,
  onToggleType,
  onPickDeal,
  isLoading,
  showRequests,
  onToggleRequests,
  showWallets,
  onToggleWallets,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Tapping anywhere outside closes the list. */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />

      <div
        className="absolute top-full z-50 mt-2 w-72 max-w-[88vw] max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-lg border border-solid border-gray-200 animate__animated animate__fadeIn"
        style={{ right: "50%", transform: "translateX(50%)" }}
        dir="rtl"
      >
        <p className="w-full text-center font-bold text-primaryColor100 text-xl py-3 m-0 border-b border-solid border-gray-200">
          الفلاتر
        </p>

        <div className="px-2 py-1">
          {isLoading && (
            <p className="text-center text-gray-500 text-sm py-3 m-0">جاري التحميل...</p>
          )}

          {!isLoading && propertyTypes.length === 0 && (
            <p className="text-center text-gray-500 text-sm py-3 m-0">
              لا توجد عروض لعرضها
            </p>
          )}

          {propertyTypes.map((propertyType) => {
            const chosenDeal = selection[propertyType];
            const checked = chosenDeal != null;

            return (
              <div
                key={propertyType}
                className="w-full flex flex-row justify-between items-start py-2 px-1 gap-3"
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => onToggleType(propertyType)}
                  className="flex flex-row items-start gap-2 bg-transparent p-0 min-w-0"
                >
                  <CheckBox checked={checked} />
                  <span
                    className={`text-base font-medium text-start break-words ${
                      checked ? "text-primaryColor100" : "text-gray-400"
                    }`}
                  >
                    {propertyType}
                  </span>
                </button>

                {/* Deal options stacked vertically so long labels never wrap. */}
                <div className="flex flex-col items-start gap-2 flex-shrink-0" role="radiogroup">
                  {DEAL_TYPES.map((deal) => (
                    <Radio
                      key={deal.id}
                      checked={chosenDeal === deal.id}
                      label={dealLabelFor(propertyType, deal.id)}
                      onClick={() => onPickDeal(propertyType, deal.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-2 py-1 border-t border-solid border-gray-200">
          <button
            type="button"
            role="checkbox"
            aria-checked={showRequests}
            onClick={onToggleRequests}
            className="w-full flex flex-row items-center gap-2 bg-transparent py-3 px-1"
          >
            <CheckBox checked={showRequests} />
            <span
              className={`text-base font-medium ${
                showRequests ? "text-primaryColor100" : "text-gray-400"
              }`}
            >
              طلبات
            </span>
          </button>

          <button
            type="button"
            role="checkbox"
            aria-checked={showWallets}
            onClick={onToggleWallets}
            className="w-full flex flex-row items-center gap-2 bg-transparent py-3 px-1"
          >
            <CheckBox checked={showWallets} />
            <span
              className={`text-base font-medium ${
                showWallets ? "text-primaryColor100" : "text-gray-400"
              }`}
            >
              محافظ
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default FiltersDropdown;
